import { Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getRequestFile, getRequestFilesDetails } from "services/driver";
import {
  driverRequestReasons,
  driverRequestStatus,
  orderStatus,
} from "enums/shared";
import ContentCard from "Components/shared/ContentCard";
import DriverRequestFile from "Components/DriversRequests/DriverRequestFile";

import LoadingBanner from "Components/shared/LoadingBanner";
import { useRTL } from "hooks/isRTL";
import { fallbackValue } from "constants/fallbackConstant";
import BackButton from "Components/shared/BackButton";
import { getDriverRequestById } from "services/driverService";

/**
 * Display detailed information about a specific driver change request.
 * Order details, request details, driver details, and associated files.
 */
const DriversRequestsInformations = () => {
  const { driverRequestId } = useParams();
  const { t } = useRTL();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["combinedDriverRequestData", driverRequestId],
    queryFn: async () => {
      const [singleRequestData, filesMetadata] = await Promise.all([
        getDriverRequestById(driverRequestId),
        getRequestFilesDetails(driverRequestId),
      ]);

      const filesWithContent = await Promise.all(
        filesMetadata.map(async (file) => {
          const content = await getRequestFile(file.id);
          return { ...file, content };
        })
      );

      return { driverRequest: singleRequestData, filesWithContent };
    },
    enabled: !!driverRequestId,
  });

  if (isLoading) {
    return <LoadingBanner isLoading={true} message="Loading..." />;
  }

  if (isError || !data?.driverRequest) {
    return (
      <Typography>
        Error fetching Driver Request Information or no data found.
      </Typography>
    );
  }

  const { driverRequest, filesWithContent } = data;

  const orderDetails = [
    {
      label: t("orderNumber"),
      value: driverRequest.orderNumber || fallbackValue,
    },
    {
      label: t("orderStatus"),
      value: t(orderStatus[driverRequest.orderStatus]?.value) || fallbackValue,
    },
  ];

  const requestDetails = [
    {
      label: t("creationDate"),
      value: driverRequest.creationDate?.split("T")[0] || fallbackValue,
    },
    {
      label: t("reason"),
      value: t(driverRequestReasons[driverRequest.reason]) || fallbackValue,
    },
    {
      label: t("driverExplanation"),
      value: driverRequest.driverExplanation || fallbackValue,
    },
    {
      label: t("status"),
      value: t(driverRequestStatus[driverRequest.status]) || fallbackValue,
    },
    {
      label: t("adminExplanation"),
      value: driverRequest.adminExplanation || fallbackValue,
    },
  ];

  const driverDetails = [
    {
      label: t("oldDriverName"),
      value: driverRequest.oldDriverName || fallbackValue,
    },
    {
      label: t("newDriverName"),
      value: driverRequest.newDriverName || fallbackValue,
    },
    {
      label: t("newDriverLocation"),
      value: driverRequest.newDriverLocation
        ? `${driverRequest.newDriverLocation.streetName}, ${driverRequest.newDriverLocation.area}, ${driverRequest.newDriverLocation.city}`
        : fallbackValue,
    },
  ];

  return (
    <Box sx={{ padding: "30px" }}>
      <BackButton text={t("backToDriverRequests")} />

      <Typography variant="h1">{t("driverRequestInformation")}</Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "20px",
        }}
      >
        <ContentCard title={t("orderDetails")} content={orderDetails} />

        <ContentCard title={t("requestDetails")} content={requestDetails} />

        <ContentCard title={t("driverDetails")} content={driverDetails} />

        <DriverRequestFile
          requestId={driverRequestId}
          fileDetails={filesWithContent}
          status={driverRequest.status}
        />
      </Box>
    </Box>
  );
};

export default DriversRequestsInformations;
